import { useEffect } from "react";
import { Outlet } from "react-router-dom";
import { AIChatWidget } from "@/domains/ai/ai-chat";
import { AIChatMobileSheet } from "@/domains/ai/ai-chat/components/ai-chat-mobile-sheet";
import { LeftSidebar } from "@/domains/app/components/left-sidebar";
import MobileTabBar from "@/domains/app/components/mobile-tab-bar";
import {
  useFCMNotifications,
  useNotificationSync,
  usePushNotifications,
} from "@/domains/notifications";
import { PausedBookingsBanner } from "@/domains/settings/features/online/components/paused-bookings-banner";
import { MobileAppLayoutProvider } from "@/lib/providers/mobile-app-layout-context";
import { StatusBarService } from "@/lib/services/status-bar-service";
import { useAppUIStore } from "./stores/app-ui-store";

interface AppLayoutProps {
  isMobile: boolean;
}

export function AppLayout({ isMobile }: AppLayoutProps) {
  // Sync notifications between new system and app UI store
  useNotificationSync();

  // Initialize FCM notifications (web)
  useFCMNotifications();

  // Initialize push notifications (unified web/native)
  usePushNotifications();

  // Access stores directly
  const notifications = useAppUIStore((state) => state.notifications);
  const setNotifications = useAppUIStore((state) => state.setNotifications);

  const outletContext = {
    notifications,
    setNotifications,
  };

  // Re-initialize status bar on mount (handles hot reload)
  useEffect(() => {
    if (isMobile) {
      StatusBarService.getInstance().initialize();
    }
  }, [isMobile]);

  // Unified Layout: Mobile-like structure for both platforms
  return (
    <MobileAppLayoutProvider>
      <div className="fixed inset-0 flex bg-page-background">
        {/* Left Sidebar - Desktop Only */}
        {!isMobile && <LeftSidebar />}

        {/* Main Content - Flex container that lets PageContent handle scrolling */}
        <div
          className="flex-1 flex flex-col overflow-x-hidden"
          style={{
            maxHeight: isMobile ? "calc(100vh - var(--keyboard-height, 0px))" : undefined,
          }}
        >
          {/* Persistent warning while online bookings are paused, with quick resume.
              Fixed to the viewport top; offsets the fixed nav bars via a CSS var. */}
          <PausedBookingsBanner />
          <Outlet context={outletContext} />
          {isMobile && <MobileTabBar />}
        </div>

        {/* AI Assistant - Different for mobile vs desktop */}
        {isMobile ? <AIChatMobileSheet /> : <AIChatWidget />}
      </div>
    </MobileAppLayoutProvider>
  );
}
